import type { Stage2TelemetryRecord } from "./stage2-telemetry-data";

export interface ForecastPoint { x_m: number; y_m: number }

export interface RaceLineForecast {
  driverIntent: ForecastPoint[];
  vehicleResponse: ForecastPoint[];
  horizonSeconds: number;
}

const advance = (
  point: ForecastPoint, heading: number, yawRate: number, speedMps: number, dt: number,
): { point: ForecastPoint; heading: number } => {
  const midpoint = heading + 0.5 * yawRate * dt;
  return {
    point: { x_m: point.x_m + speedMps * dt * Math.cos(midpoint), y_m: point.y_m + speedMps * dt * Math.sin(midpoint) },
    heading: heading + yawRate * dt,
  };
};

/**
 * A display-only observation rollout. Driver intention uses the frozen S1-A
 * classical driver-model output for 0.8 seconds. Vehicle response integrates
 * the frozen classical vehicle response and recorded observation speed for
 * 2.5 seconds. Neither series feeds back into replay control. The supplied
 * driver output is already the effective steering angle, so the car-specific
 * steering sign must not be applied a second time.
 * Effective steering and vehicle yaw are clockwise-positive, while the local
 * x/y display uses counter-clockwise-positive headings; both are converted
 * exactly once at this boundary.
 */
export const buildRaceLineForecast = (
  record: Stage2TelemetryRecord, horizonSeconds = 2.5, dtSeconds = 0.1,
): RaceLineForecast => {
  const steps = Math.max(1, Math.round(horizonSeconds / dtSeconds));
  const intentSteps = Math.min(steps, Math.max(1, Math.round(
    record.driver_intent.intent_horizon_ms / 1_000 / dtSeconds,
  )));
  const smoothingTime = record.driver_intent.smoothing_time_seconds;
  const rollout = record.driver_intent.steering_rollout;
  const rolloutValueAt = (timeSeconds: number, key: "steering_deg" | "speed_kmh" | "vehicle_yaw_response_rad_s") => {
    const timeMs = timeSeconds * 1_000;
    if (timeMs <= rollout[0].offset_ms) return rollout[0][key];
    if (timeMs >= rollout.at(-1)!.offset_ms) return rollout.at(-1)![key];
    let low = 0, high = rollout.length - 1;
    while (low + 1 < high) {
      const middle = Math.floor((low + high) / 2);
      if (rollout[middle].offset_ms <= timeMs) low = middle;
      else high = middle;
    }
    const ratio = (timeMs - rollout[low].offset_ms) / (rollout[high].offset_ms - rollout[low].offset_ms);
    return rollout[low][key] + (rollout[high][key] - rollout[low][key]) * ratio;
  };
  const rawTargetYawAt = (timeSeconds: number) =>
    -record.driver_intent.turn_gain_rad_per_s_per_deg * rolloutValueAt(timeSeconds, "steering_deg");
  const sustainedTargetYawAt = (timeSeconds: number) => {
    // Average only a short forward window. This removes steering-wheel
    // balancing chatter without averaging a deliberate formation-lap weave
    // or the opposite turn later in the 2.5 s forecast into the current intent.
    const windowSeconds = Math.min(0.4, smoothingTime);
    const sampleStep = Math.min(0.1, dtSeconds);
    let weighted = 0, total = 0;
    for (let offset = 0; offset <= windowSeconds + 1e-9; offset += sampleStep) {
      const weight = Math.max(0.05, 1 - offset / Math.max(windowSeconds, sampleStep));
      weighted += weight * rawTargetYawAt(timeSeconds + offset);
      total += weight;
    }
    return total > 0 ? weighted / total : rawTargetYawAt(timeSeconds);
  };
  const start = { x_m: record.pose.x_m, y_m: record.pose.y_m };
  const driverIntent = [start];
  const vehicleResponse = [start];
  let intentPoint = start, responsePoint = start;
  let intentHeading = record.pose.heading_rad, responseHeading = record.pose.heading_rad;
  for (let step = 1; step <= steps; step += 1) {
    const midpointTime = (step - 0.5) * dtSeconds;
    const speedMps = Math.max(0, rolloutValueAt(midpointTime, "speed_kmh") / 3.6);
    if (step <= intentSteps) {
      const intentYaw = sustainedTargetYawAt(midpointTime);
      const intent = advance(intentPoint, intentHeading, intentYaw, speedMps, dtSeconds);
      intentPoint = intent.point; intentHeading = intent.heading; driverIntent.push(intentPoint);
    }
    const responseYaw = -rolloutValueAt(midpointTime, "vehicle_yaw_response_rad_s");
    const response = advance(responsePoint, responseHeading, responseYaw, speedMps, dtSeconds);
    responsePoint = response.point; responseHeading = response.heading; vehicleResponse.push(responsePoint);
  }
  return { driverIntent, vehicleResponse, horizonSeconds: steps * dtSeconds };
};
